import DynamicFormRenderer from '@aims/shared/report-schedules/DynamicFormRenderer';
import {
  reportScheduleForUserQuery,
  allReportScheduleTemplatesForUserQuery,
} from '@aims/shared/report-schedules/constants';
import { generateUuid } from '@aims/shared/shared/utils';
import {
  createScheduledReportForUserMutation,
  updateScheduledReportForUserMutation,
  submitScheduledReportForUserMutation,
  scheduledReportForUserQuery,
} from '@aims/shared/report-schedules/scheduled-report-constants';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Card,
  Descriptions,
  Input,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import CSPage from '../../../components/CSPage';
import CSPageHeader from '../../../components/CSPageHeader';
import useQueryParams from '../../../shared/use-query-params';
import {
  ScheduleFrequencies,
  ScheduleEntryStatuses,
} from '@aims/shared/report-schedules/constants';

const { Text } = Typography;
const { TextArea } = Input;

function FillScheduledReportPage() {
  const { projectId, entryId } = useParams();
  const query = useQueryParams();
  const scheduleId = query?.scheduleId;
  const history = useHistory();

  const [formData, setFormData] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [reportId, setReportId] = useState(null);

  // Load schedule
  const { data: scheduleData, loading: loadingSchedule } = useQuery(
    reportScheduleForUserQuery,
    {
      variables: { _id: scheduleId },
      skip: !scheduleId,
    },
  );
  const schedule = useMemo(
    () => scheduleData?.reportScheduleForUser || null,
    [scheduleData],
  );

  // Find the entry
  const entry = useMemo(() => {
    if (!schedule?.entries) return null;
    return schedule.entries.find((e) => e.entryId === entryId);
  }, [schedule, entryId]);

  // Load template for this entry
  const templateId = entry?.templateId;
  const { data: templatesData, loading: loadingTemplates } = useQuery(
    allReportScheduleTemplatesForUserQuery,
    {
      variables: { first: 200 },
      fetchPolicy: 'cache-first',
    },
  );
  const template = useMemo(() => {
    if (!templatesData?.allReportScheduleTemplatesForUser?.edges || !templateId)
      return null;
    return templatesData.allReportScheduleTemplatesForUser.edges.find(
      (e) => e.node._id === templateId,
    )?.node;
  }, [templatesData, templateId]);

  // Check if there's already a report for this entry (editing existing)
  const { data: existingReportData, loading: loadingExisting } = useQuery(
    scheduledReportForUserQuery,
    {
      variables: { _id: entry?.scheduledReportId },
      skip: !entry?.scheduledReportId,
    },
  );
  const existingReport = useMemo(
    () => existingReportData?.scheduledReportForUser || null,
    [existingReportData],
  );

  useEffect(() => {
    if (existingReport) {
      setReportId(existingReport._id);
      setFormData(existingReport.formData || {});
      setNotes(existingReport.notes || '');
    }
  }, [existingReport]);

  const [createReport] = useMutation(createScheduledReportForUserMutation);
  const [updateReport] = useMutation(updateScheduledReportForUserMutation);
  const [submitReport] = useMutation(submitScheduledReportForUserMutation);

  const handleFieldChange = useCallback((fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving('Saving ...');
    try {
      if (reportId) {
        await updateReport({
          variables: {
            report: {
              _id: reportId,
              formData,
              notes,
            },
          },
        });
      } else {
        const result = await createReport({
          variables: {
            report: {
              _id: generateUuid(),
              reportType: 'SCHEDULED_REPORT',
              scheduleId,
              scheduleEntryId: entryId,
              templateId: entry?.templateId,
              frequency: entry?.frequency,
              dueDate: entry?.dueDate,
              holisticProjectId: projectId,
              formData,
              notes,
            },
          },
        });
        const newId = result?.data?.createScheduledReportForUser?.report?._id;
        if (newId) setReportId(newId);
      }
      notification.success({
        message: 'Saved',
        description: 'Your report has been saved as a draft.',
      });
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error',
        description: err.message,
      });
    }
    setSaving(false);
  }, [
    reportId,
    formData,
    notes,
    scheduleId,
    entryId,
    entry,
    projectId,
    createReport,
    updateReport,
  ]);

  const handleSubmit = useCallback(async () => {
    setSaving('Submitting ...');
    try {
      // Save first
      if (reportId) {
        await updateReport({
          variables: {
            report: {
              _id: reportId,
              formData,
              notes,
            },
          },
        });
      } else {
        const result = await createReport({
          variables: {
            report: {
              _id: generateUuid(),
              reportType: 'SCHEDULED_REPORT',
              scheduleId,
              scheduleEntryId: entryId,
              templateId: entry?.templateId,
              frequency: entry?.frequency,
              dueDate: entry?.dueDate,
              holisticProjectId: projectId,
              formData,
              notes,
            },
          },
        });
        const newId = result?.data?.createScheduledReportForUser?.report?._id;
        if (newId) {
          setReportId(newId);
          // Now submit
          await submitReport({
            variables: { reportId: newId },
          });
          notification.success({
            message: 'Submitted',
            description: 'Your report has been submitted for review.',
          });
          history.push(`/holistic-projects/${projectId}/reports`);
          return;
        }
      }
      // Submit existing
      if (reportId) {
        await submitReport({
          variables: { reportId },
        });
        notification.success({
          message: 'Submitted',
          description: 'Your report has been submitted for review.',
        });
        history.push(`/holistic-projects/${projectId}/reports`);
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error',
        description: err.message,
      });
    }
    setSaving(false);
  }, [
    reportId,
    formData,
    notes,
    scheduleId,
    entryId,
    entry,
    projectId,
    createReport,
    updateReport,
    submitReport,
    history,
  ]);

  const loading = loadingSchedule || loadingTemplates || loadingExisting;

  const submitTooEarly = useMemo(() => {
    if (!entry?.dueDate) return false;
    const daysUntilDue = Math.abs(dayjs(entry.dueDate).diff(dayjs(), 'day'));
    return daysUntilDue > 3;
  }, [entry]);

  if (loading) {
    return (
      <CSPage title="AIMS.org | Fill Out Report">
        <CSPageHeader
          title="Fill Out Report"
          parents={[
            { label: 'Holistic Projects', link: '/holistic-projects' },
            {
              label: 'Project',
              link: `/holistic-projects/${projectId}/reports`,
            },
          ]}
        />
        <Skeleton active />
      </CSPage>
    );
  }

  return (
    <CSPage title="AIMS.org | Fill Out Report">
      <CSPageHeader
        title={entry?.label || 'Fill Out Report'}
        parents={[
          { label: 'Holistic Projects', link: '/holistic-projects' },
          {
            label: 'Project',
            link: `/holistic-projects/${projectId}/reports`,
          },
        ]}
      />

      <Card style={{ marginBottom: 24 }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Frequency">
            <Tag color={ScheduleFrequencies[entry?.frequency]?.color}>
              {ScheduleFrequencies[entry?.frequency]?.label || entry?.frequency}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Due Date">
            {entry?.dueDate ? dayjs(entry.dueDate).format('MMM D, YYYY') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={ScheduleEntryStatuses[entry?.status]?.color}>
              {ScheduleEntryStatuses[entry?.status]?.label || entry?.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Template">
            {template?.name || 'Unknown'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {template?.formFields?.length > 0 ? (
        <Card title="Report Form" style={{ marginBottom: 24 }}>
          <DynamicFormRenderer
            formFields={template.formFields}
            formData={formData}
            onChange={handleFieldChange}
          />
        </Card>
      ) : (
        <Card style={{ marginBottom: 24 }}>
          <Text type="secondary">
            No form template has been configured for this report frequency.
          </Text>
        </Card>
      )}

      <Card title="Notes" style={{ marginBottom: 24 }}>
        <TextArea
          rows={4}
          placeholder="Add any notes or comments about this report..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Card>

      <Space>
        <Button
          type="primary"
          onClick={handleSave}
          loading={saving === 'Saving ...'}
          disabled={!!saving}
        >
          Save Draft
        </Button>
        <Tooltip
          title={
            submitTooEarly
              ? 'Reports can only be submitted within 3 days of the due date'
              : undefined
          }
        >
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={saving === 'Submitting ...'}
            disabled={!!saving || submitTooEarly}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          >
            Submit Report
          </Button>
        </Tooltip>
        <Button
          onClick={() =>
            history.push(`/holistic-projects/${projectId}/reports`)
          }
        >
          Cancel
        </Button>
      </Space>
    </CSPage>
  );
}

export default FillScheduledReportPage;
